import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, LogOut, ArrowLeft, Send, Clock, CheckCircle, XCircle, History, Download, FileText } from 'lucide-react';
import axios from 'axios';
import { exportToCSV, downloadReceipt } from '@/utils/exportTransactions';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransactionsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions?user_id=${user.id}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-amber-100 text-amber-700';
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Navigation */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png" 
                  alt="RouteFlow AI" 
                  className="h-10 w-auto"
                />
              </div>
            </div>
            <Button variant="ghost" onClick={onLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Transactions List */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Transaction History</h1>
              <p className="text-gray-600">View all your transfers and their status</p>
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline"
                onClick={() => {
                  exportToCSV(transactions);
                  toast.success('Transactions exported to CSV!');
                }}
                disabled={transactions.length === 0}
              >
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
              <Button onClick={() => navigate('/transfer')} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" data-testid="new-transfer-btn">
                <Send className="w-4 h-4 mr-2" /> Send Money
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : transactions.length === 0 ? (
          <Card className="border-0 shadow-lg" data-testid="no-transactions-card">
            <CardContent className="flex flex-col items-center justify-center py-12 text-gray-500">
              <History className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-semibold mb-2">No transactions yet</p>
              <p className="mb-4">Start by sending your first transfer!</p>
              <Button onClick={() => navigate('/transfer')} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" data-testid="start-transfer-btn">
                Send Money
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {transactions.map((txn) => (
              <Card key={txn.id} className="border-0 shadow-lg hover:shadow-xl smooth-transition" data-testid={`transaction-card-${txn.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Transaction Info */}
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Send className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-lg">
                            {txn.from_country} → {txn.to_country}
                          </h3>
                          <Badge className={getStatusColor(txn.status)} data-testid={`status-badge-${txn.id}`}>
                            {txn.status}
                          </Badge>
                        </div>
                        <p className="text-gray-600 text-sm mb-1">To: {txn.recipient_name}</p>
                        <p className="text-gray-500 text-xs">Account: {txn.recipient_account}</p>
                        <p className="text-gray-500 text-xs mt-2">
                          {format(new Date(txn.created_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>

                    {/* Amount and Route */}
                    <div className="flex flex-col md:items-end space-y-2">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900" data-testid={`amount-${txn.id}`}>
                          ${txn.amount.toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {txn.from_currency} → {txn.to_currency}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-gray-600">via {txn.route}</p>
                        <p className="text-gray-500">Network Fee: ${txn.fees.toFixed(2)}</p>
                        {txn.service_fee && (
                          <p className="text-emerald-600">Service Fee: ${txn.service_fee.toFixed(2)}</p>
                        )}
                        {txn.total_amount && (
                          <p className="text-gray-900 font-semibold">Total: ${txn.total_amount.toFixed(2)}</p>
                        )}
                        <p className="text-gray-500">{txn.estimated_time}</p>
                      </div>
                    </div>

                    {/* Status Icon */}
                    <div className="flex md:flex-col items-center justify-center">
                      {getStatusIcon(txn.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsPage;